import React, { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import cx from 'classnames';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextareaAutosize from 'react-textarea-autosize';
import { ReactComponent as SendIcon } from '../../assets/img/icons/assistant/Send.svg';
import { LargeIconButton, SideModal } from '..';
import { getAccessToken } from '../../utils/authorization';
import useIsMobile from '../../hooks/useIsMobile';
import usePrevious from '../../hooks/usePrevious';
import AIAssistantHeader from './AIAssistantHeader';
import AIAssistantChatHistory from './AIAssistantChatHistory';
import AIAssistantChatHistoryEmpty from './AIAssistantChatHistoryEmpty';
import AIAssistantInitializing from './AIAssistantInitializing';
import AIAssistantWarning from './AIAssistantWarning';
import AIAssistantError from './AIAssistantError';
import './AIAssistant.scss';

const MAX_INIT_CONNECTION_ATTEMPTS = 5;

const AIAssistantChat = ({ onCancel, userUuid, restaurantUuid, userType }) => {
  const [status, setStatus] = useState('initializing');
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();
  const prevStatus = usePrevious(status);
  const firstMessageIdRef = useRef();
  const hasAllHistoryRef = useRef(false);
  const loadingRef = useRef(false);
  const modalRef = useRef();
  const bodyRef = useRef();
  const bodyTopOffsetRef = useRef(0);
  const socketRef = useRef();
  const animationTimerRef = useRef();
  const currentInitConnectionAttemptRef = useRef(0);
  const isInitializing = status === 'initializing';
  const isReady = status === 'ready';
  const isPrevReady = prevStatus === 'ready';
  let bodyContent;
  let controls = '';

  if (chatHistory[0]?.id) {
    firstMessageIdRef.current = chatHistory[0].id;
  }

  const sendMessage = () => {
    const validMessage = message.trim();
    
    if (socketRef.current && validMessage) {
      socketRef.current.emit('text_message', validMessage);
    }

    setMessage('');
  };

  const onChangeInput = event => {
    setMessage(event.target.value);
  };

  const onPressEnter = event => {
    if (event.keyCode === 13 && event.shiftKey === false && message) {
      event.preventDefault();
      sendMessage();
    }
  };
  
  const clearHistory = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('delete_history');
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight });
    }
  }, []);

  const addNewMessage = useCallback(
    (wsMsg, type) => {
      const newMessage = {
        id: uuidv4(),
        text: wsMsg.text,
        html: wsMsg.html || null,
        time: wsMsg.timestamp,
        type,
      };

      setChatHistory(prev => [...prev, newMessage]);
      scrollToBottom();
    },
    [scrollToBottom]
  );
  
  const runDatesAnimation = useCallback(() => {
    if (!loadingRef.current && bodyRef.current) {
      const historyDates = bodyRef.current.querySelectorAll('.ai-assistant-chat-history-date');
      const bodyTopOffset = bodyTopOffsetRef.current;
      
      for (const historyDate of historyDates) {
        const historyDateTopOffset = historyDate.getBoundingClientRect().top;
        
        if (historyDateTopOffset <= bodyTopOffset && bodyRef.current.scrollTop > 0) {
          historyDate.classList.remove('ai-assistant-chat-history-date-fade-out');
          void historyDate.offsetWidth;
          historyDate.classList.add('ai-assistant-chat-history-date-fade-out');
        } else {
          historyDate.classList.remove('ai-assistant-chat-history-date-fade-out');
        }
      }
    }
  }, []);

  const onScroll = useCallback(event => {
    if (
      !hasAllHistoryRef.current &&
      !loadingRef.current &&
      event.target.scrollTop === 0 &&
      socketRef.current &&
      firstMessageIdRef.current
    ) {
      const hasAnimation = Boolean(modalRef.current?.classList.contains('ai-assistant-animation'));

      if (!hasAnimation) {
        setLoading(true);
        loadingRef.current = true;
        socketRef.current.emit('history', { lastMessage: firstMessageIdRef.current });
      }
    }
    
    runDatesAnimation();
  }, [runDatesAnimation]);

  const onConnectError = useCallback(() => {
    let disconnect = false;

    if (!socketRef.current?.active) {
      disconnect = true;
    } else if (isInitializing) {
      if (currentInitConnectionAttemptRef.current >= MAX_INIT_CONNECTION_ATTEMPTS) {
        disconnect = true;
      } else {
        currentInitConnectionAttemptRef.current += 1;
      }
    }

    if (disconnect) {
      socketRef.current?.disconnect();
      setStatus('disconnected');
    }
  }, [isInitializing]);

  const onDisconnect = useCallback(() => {
    if (socketRef.current && socketRef.current.active) {
      setStatus('reconnecting');
    } else {
      socketRef.current?.disconnect();
      setStatus('disconnected');
    }
  }, []);

  const onChatHistory = useCallback(
    wsMsg => {
      if (wsMsg.isFirstHistory) {
        setChatHistory(wsMsg.history);
        setStatus('ready');
        scrollToBottom();
      } else {
        const newChatHistoryLen = wsMsg.history.length;
        const newChatHistory = wsMsg.history.splice(0, newChatHistoryLen - 1);
        const prevScrollHeight = bodyRef.current?.scrollHeight || 0;

        if (newChatHistoryLen < 20 && bodyRef.current) {
          hasAllHistoryRef.current = true;
        }

        setChatHistory(prev => [...newChatHistory, ...prev]);

        if (bodyRef.current) {
          bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight - prevScrollHeight });
        }
  
        setLoading(false);
        loadingRef.current = false;
        runDatesAnimation();
      }
    },
    [onScroll, scrollToBottom, runDatesAnimation]
  );

  const onMessage = useCallback(
    wsMsg => {
      addNewMessage(wsMsg, 'bot');
    },
    [addNewMessage]
  );

  const onGetTextFromClient = useCallback(
    wsMsg => {
      addNewMessage(wsMsg, 'user');
    },
    [addNewMessage]
  );

  const onChatHistoryDeleted = useCallback(() => {
    setChatHistory([]);
  }, []);
  
  useLayoutEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.addEventListener('scroll', onScroll);
    }
    
    return () => {
      if (bodyRef.current) {
        bodyRef.current.removeEventListener('scroll', onScroll);
      }
    };
  }, [onScroll]);
  
  useLayoutEffect(() => {
    if (modalRef.current && bodyRef.current) {
      const activeAnimation = !isPrevReady && isReady;

      if (activeAnimation) {
        clearTimeout(animationTimerRef.current);
        modalRef.current.classList.add('ai-assistant-animation');
        
        const lastMsg = bodyRef.current.querySelector('.ai-assistant-msg[data-animation-delay]');
        const maxAnimationDelay = Number(lastMsg?.dataset.animationDelay) || 2000;
        let maxAnimationDuration = 1000 + maxAnimationDelay; // 1000 is the containerItemsAnimation duration. See AIAssistant.scss
        
        if (maxAnimationDuration > 3000) {
          maxAnimationDuration = 3000;
        }
  
        animationTimerRef.current = setTimeout(() => {
          modalRef.current.classList.remove('ai-assistant-animation');
        }, maxAnimationDuration);
      }
    }
  }, [isPrevReady, isReady]);

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_CFA_PROXY, {
      transports: ['websocket'],
      auth: {
        staticSid: userUuid,
        withWhisper: false,
        deviceType: 'web',
        clientMode: userType,
      },
      path: '/chat',
      query: {
        restaurantUuid,
        Authorization: getAccessToken(),
      },
    });
    
    if (bodyRef.current) {
      bodyTopOffsetRef.current = bodyRef.current.getBoundingClientRect().top;
    }

    return () => {
      socketRef.current.disconnect();
      clearTimeout(animationTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.on('connect_error', onConnectError);
      socketRef.current.on('disconnect', onDisconnect);
      socketRef.current.on('json', onChatHistory);
      socketRef.current.on('message', onMessage);
      socketRef.current.on('get_text_from_client', onGetTextFromClient);
      socketRef.current.on('delete_history_completed', onChatHistoryDeleted);
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect_error', onConnectError);
        socketRef.current.off('disconnect', onDisconnect);
        socketRef.current.off('json', onChatHistory);
        socketRef.current.off('message', onMessage);
        socketRef.current.off('get_text_from_client', onGetTextFromClient);
        socketRef.current.off('delete_history_completed', onChatHistoryDeleted);
      }
    };
  }, [onConnectError, onDisconnect, onChatHistory, onMessage, onGetTextFromClient, onChatHistoryDeleted]);

  switch (status) {
    case 'initializing': {
      bodyContent = <AIAssistantInitializing />;
      break;
    }
    case 'reconnecting': {
      bodyContent = <AIAssistantWarning />;
      break;
    }
    case 'disconnected': {
      bodyContent = <AIAssistantError />;
      break;
    }
    case 'ready': {
      bodyContent = (
        <>
          {loading ? (
            <div className="ai-assistant-chat-history-loading">
              <FontAwesomeIcon icon={faSpinner} size="lg" spin />
            </div>
          ) : null}
          
          {chatHistory.length ? (
            <AIAssistantChatHistory chatHistory={chatHistory} />
          ) : (
            <AIAssistantChatHistoryEmpty />
          )}
        </>
      );
  
      controls = (
        <div className="ai-assistant-controls">
          <TextareaAutosize
            minRows={1}
            maxRows={5}
            className="form-textarea"
            placeholder="Ask something"
            value={message}
            onChange={onChangeInput}
            onKeyDown={onPressEnter}
            disabled={!isReady}
          />
      
          <LargeIconButton
            Icon={SendIcon}
            className="ai-assistant-btn-send"
            disabled={!isReady || !message}
            onClick={sendMessage}
          />
        </div>
      );
      
      break;
    }
    default: {
      bodyContent = <AIAssistantError />;
    }
  }
  
  const header = (
    <AIAssistantHeader hasDropdown={isReady} hasHistory={!!chatHistory.length} clearHistory={clearHistory} />
  );

  const body = <div className="ai-assistant-body">{bodyContent}</div>;

  return (
    <SideModal
      customTitle={header}
      onCancel={onCancel}
      className={cx('ai-assistant', { 'ai-assistant-desktop': !isMobile })}
      modalRef={modalRef}
      bodyRef={bodyRef}
      modalBody={body}
      customButtons={controls}
    />
  );
};

export default AIAssistantChat;
